import React from "react";

import { humanize, moneySaved, toNumber } from "../../lib/format";
import type {
  InsightDimension,
  InsightFilters,
  OfflineUsage,
} from "../../lib/types";

export const OTHER = "__other__";
export const NO_VALUE = "__none__";

// Dimensions whose "no value" row can be filtered to; person, team and key
// filters take IDs.
const NONE_FILTERABLE = new Set<InsightDimension>([
  "repo",
  "model",
  "kind",
  "client",
  "tenant",
  "feature",
  "env",
  "device",
]);

/** The filter value a breakdown row narrows to, or null when it can't. */
export function drillId(
  dim: InsightDimension,
  key: string | null,
): string | null {
  if (key === OTHER) return null;
  if (key == null || key === NO_VALUE)
    return NONE_FILTERABLE.has(dim) ? NO_VALUE : null;
  return key;
}

export const WORK: Record<string, string> = {
  coding: "Coding",
  exploration: "Exploration",
  shell_ops: "Shell & ops",
  chat: "Chat & planning",
  mixed: "Mixed",
};
const SLOTS = 7;
const OTHER_COLOR = "var(--ins-other)";

export function usd(v: string | number | null | undefined): string {
  const n = toNumber(v);
  return `${n < 0 ? "-" : ""}$${moneySaved(Math.abs(n))}`;
}

// $1.2M / $584.7K / $625 / $12.40 — compact for tiles and axes, cents below $100.
export function usdShort(v: string | number | null | undefined): string {
  const n = toNumber(v);
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (a === 0) return "$0";
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  if (a >= 100) return `${sign}$${Math.round(a).toLocaleString("en-US")}`;
  return `${sign}$${a.toFixed(2)}`;
}

/** Relative change from ``prev`` to ``now``; null when there's no base. */
export function change(now: number, prev: number): number | null {
  if (!Number.isFinite(prev) || prev === 0) return null;
  return (now - prev) / Math.abs(prev);
}

/** The window of the same length that ends the day before ``range`` starts. */
export function previousRange(range: { start: string; end: string }): {
  start: string;
  end: string;
} {
  const day = 86_400_000;
  const s = Date.parse(range.start + "T00:00:00Z");
  const e = Date.parse(range.end + "T00:00:00Z");
  const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
  return { start: iso(s - (e - s) - day), end: iso(s - day) };
}

export function pct(v: number | null | undefined, digits = 0): string {
  return v == null ? "—" : `${(v * 100).toFixed(digits)}%`;
}

export function shortDay(iso: string): string {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function timeOf(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Gives each live key a categorical slot, keeping any slot a key held before
// unless another live key already claimed it. Mutates and returns ``map``.
export function assignSlots(
  map: Map<string, number>,
  keys: string[],
): Map<string, number> {
  const claimed = new Set<number>();
  const fresh: string[] = [];
  for (const k of keys) {
    if (k === OTHER) continue;
    const s = map.get(k);
    if (s !== undefined && !claimed.has(s)) claimed.add(s);
    else fresh.push(k);
  }
  for (const k of fresh) {
    let s = 0;
    while (claimed.has(s)) s++;
    if (s >= SLOTS) {
      map.delete(k);
      continue;
    }
    map.set(k, s);
    claimed.add(s);
  }
  return map;
}

// A series keeps its colour for as long as the view lives: re-ranking after a
// filter or range change must not repaint the entities that survive it.
export function useStableColors(scopeKey: string) {
  const slots = React.useRef<{ scope: string; map: Map<string, number> }>({
    scope: scopeKey,
    map: new Map(),
  });
  if (slots.current.scope !== scopeKey)
    slots.current = { scope: scopeKey, map: new Map() };
  return React.useCallback((keys: string[]) => {
    const map = assignSlots(slots.current.map, keys);
    const live = new Set(keys);
    return (key: string) => {
      const s = live.has(key) ? map.get(key) : undefined;
      return s === undefined ? OTHER_COLOR : `var(--ins-s${s + 1})`;
    };
  }, []);
}

export function useLoad<T>(
  load: () => Promise<T>,
  deps: React.DependencyList,
): { data: T | null; error: string | null; loading: boolean } {
  const [state, setState] = React.useState<{
    data: T | null;
    error: string | null;
    loading: boolean;
  }>({ data: null, error: null, loading: true });
  React.useEffect(() => {
    let live = true;
    setState({ data: null, error: null, loading: true });
    load()
      .then((data) => live && setState({ data, error: null, loading: false }))
      .catch(
        (e: Error) =>
          live && setState({ data: null, error: e.message, loading: false }),
      );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export type ActiveFilter = {
  key: keyof InsightFilters;
  value: string;
  label: string;
};

export const DIMENSION_LABELS: Record<InsightDimension, string> = {
  none: "Total",
  user: "Person",
  team: "Team",
  repo: "Repository",
  model: "Model",
  kind: "Agent",
  client: "Agent version",
  cost_basis: "Billing",
  tenant: "Customer",
  feature: "Feature",
  env: "Environment",
  key: "API key",
  device: "Device",
};

/** One quiet line for tokens that bypassed Condense; null when there were none. */
export function offlineNote(o: OfflineUsage | null | undefined): string | null {
  if (!o || o.requests <= 0) return null;
  const est = toNumber(o.estimated_cost_usd);
  const worth =
    est > 0 ? `, about ${usdShort(est)} estimated at list rates` : "";
  return `${humanize(o.total_tokens)} tokens ran outside Condense${worth}. Not in the cost figures.`;
}

// The one definition of cache hit, on every page that shows it.
export const CACHE_HIT_TIP =
  "Cache read ÷ (input + cache read + cache write): the share of prompt tokens served from the provider's cache.";

export function filtersOf(active: ActiveFilter[]): InsightFilters {
  return Object.fromEntries(active.map((f) => [f.key, f.value]));
}

export { humanize };

/** Shortens a long name, keeping a path's last segment whole when it fits:
 * "freshorg/platform-api" → "fresh…/platform-api". */
export function midTrunc(s: string, max = 24): string {
  if (s.length <= max) return s;
  const tail = s.slice(s.lastIndexOf("/"));
  if (s.includes("/") && tail.length + 4 <= max)
    return `${s.slice(0, max - tail.length - 1)}…${tail}`;
  const head = Math.ceil((max - 1) * 0.6);
  return `${s.slice(0, head)}…${s.slice(s.length - (max - 1 - head))}`;
}

/** "mcp__playwright__browser_click" reads as "Playwright: browser click". */
const KIND_NAMES: Record<string, string> = {
  claude_code: "Claude Code",
  opencode: "OpenCode",
  api: "API",
};

/** An agent kind id as people say it: claude_code → Claude Code. */
export function kindLabel(kind: string): string {
  return (
    KIND_NAMES[kind] ??
    kind
      .split(/[_-]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  );
}

export function toolLabel(key: string): string {
  const m = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(key);
  if (!m) return key;
  const server = m[1].replace(/[_-]+/g, " ");
  return `${server[0].toUpperCase()}${server.slice(1)}: ${m[2].replace(/_/g, " ")}`;
}

/** "github.com/acme/checkout" → "acme/checkout": the host repeats on every row. */
export function shortRepo(label: string): string {
  const parts = label.split("/");
  return parts.length > 2 && parts[0].includes(".")
    ? parts.slice(1).join("/")
    : label;
}
